import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import { useEffect, useState } from "react";
import { TopBar } from "./layout/TopBar";
import { Sidebar } from "./layout/Sidebar";
import { SalienceSidebar } from "./layout/SalienceSidebar";
import { MobileNav } from "./layout/SectionNav";
import { ChatPanel } from "./layout/ChatPanel";
import { Overview } from "./pages/Overview";
import { PortalHome } from "./pages/PortalHome";
import { VideoDetail } from "./pages/VideoDetail";
import { CommentsDashboard } from "./pages/CommentsDashboard";
import { ThemesManager } from "./pages/ThemesManager";
import { YouTubeMethods } from "./pages/YouTubeMethods";
import { SalienceEvalMarker } from "./pages/SalienceEvalMarker";
import { SalienceOverview } from "./pages/SalienceOverview";
import { SalienceTermDetail } from "./pages/SalienceTermDetail";
import { SalienceMethods } from "./pages/SalienceMethods";
import { DiscordPreview } from "./pages/preview/DiscordPreview";
import { InboxPreview } from "./pages/preview/InboxPreview";
import { SettingsUsers } from "./pages/SettingsUsers";
import { SignIn } from "./pages/SignIn";
import { redirectToSignIn, useSession } from "./lib/auth";

// Back-compat: rewrite pre-6d flat /videos/:videoId paths to /youtube/...
function VideoLegacyRedirect() {
  const { videoId, version } = useParams<{ videoId: string; version?: string }>();
  const target = version
    ? `/youtube/videos/${videoId}/digests/${version}`
    : `/youtube/videos/${videoId}`;
  return <Navigate to={target} replace />;
}

export function App() {
  const [chatOpen, setChatOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const inSalience = location.pathname.startsWith("/salience");
  // The portal home ("/") and the future-module PREVIEW pages are full-width
  // cross-module surfaces — no per-module sidebar, the main column takes the
  // full width.
  const isHome = location.pathname === "/";
  const isPreview = location.pathname.startsWith("/preview");
  // Settings is likewise cross-module: no per-module sidebar.
  const isSettings = location.pathname.startsWith("/settings");
  const fullWidth = isHome || isPreview || isSettings;

  // Auto-close mobile drawers on navigation so tapping a video in the
  // sidebar drawer closes it without an extra dismiss step.
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Sign-in (#27). The auth pages render without portal chrome. Everything
  // else waits for /api/auth/me; when the proxy enforces sign-in and there's
  // no session, go straight to the broker sign-in (a full navigation; API calls would
  // 401 anyway — this just avoids flashing an empty portal first).
  const session = useSession();
  const needsSignIn = !!session.data?.authRequired && !session.data.user;
  useEffect(() => {
    if (needsSignIn) redirectToSignIn();
  }, [needsSignIn]);
  const isAuthPage =
    location.pathname === "/signin" || location.pathname.startsWith("/auth/");
  if (isAuthPage) {
    return (
      <Routes>
        <Route path="/signin" element={<SignIn />} />
        <Route path="*" element={<Navigate to="/signin" replace />} />
      </Routes>
    );
  }
  if (session.isPending) {
    return <div className="h-full bg-paper" />;
  }
  if (needsSignIn) {
    return <div className="h-full bg-paper" />;
  }

  return (
    <div className="grid h-full grid-rows-[56px_1fr] bg-paper">
      <TopBar
        onMenuClick={() => setSidebarOpen(true)}
        onChatClick={() => setChatOpen(true)}
      />

      {/*
        Mobile (default): flex container; Sidebar + ChatPanel render as
        fixed-positioned drawers (out of flow); main pane takes full width.
        md+ : grid container with three columns (two on the home page, which
        has no sidebar); sidebar/chat are in-flow.
      */}
      <div
        className={`relative flex h-full overflow-hidden md:grid ${
          fullWidth
            ? "md:grid-cols-[1fr_auto]"
            : "md:grid-cols-[280px_1fr_auto]"
        }`}
      >
        {fullWidth ? (
          // Full-width surfaces (home, concept previews) have no module
          // sidebar — the burger opens a lightweight section-nav drawer.
          <MobileNav
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
          />
        ) : inSalience ? (
          <SalienceSidebar
            mobileOpen={sidebarOpen}
            onMobileClose={() => setSidebarOpen(false)}
          />
        ) : (
          <Sidebar
            mobileOpen={sidebarOpen}
            onMobileClose={() => setSidebarOpen(false)}
          />
        )}

        {/* min-w-0 lets this flex/grid child shrink to the viewport instead of
            being widened by a wide descendant (charts report a slightly-wide
            measured width); overflow-x-hidden clips any residual sideways
            overflow so mobile only pans vertically. */}
        <main className="min-w-0 flex-1 overflow-x-hidden overflow-y-auto bg-paper md:flex-auto">
          <div className="mx-auto min-w-0 max-w-285 px-4 py-6 sm:px-8 sm:py-8">
            <Routes>
              <Route path="/" element={<PortalHome />} />
              <Route path="/settings" element={<Navigate to="/settings/users" replace />} />
              <Route path="/settings/users" element={<SettingsUsers />} />
              <Route path="/youtube" element={<Overview />} />
              <Route path="/youtube/comments" element={<CommentsDashboard />} />
              <Route path="/youtube/themes" element={<ThemesManager />} />
              <Route path="/youtube/methods" element={<YouTubeMethods />} />
              <Route path="/salience" element={<SalienceOverview />} />
              <Route
                path="/salience/terms/:termId"
                element={<SalienceTermDetail />}
              />
              <Route path="/salience/methods" element={<SalienceMethods />} />
              <Route path="/salience/eval" element={<SalienceEvalMarker />} />
              {/* Future-module concept previews (non-functional mockups). */}
              <Route path="/preview/discord" element={<DiscordPreview />} />
              <Route path="/preview/inbox" element={<InboxPreview />} />
              <Route path="/youtube/videos/:videoId" element={<VideoDetail />} />
              <Route
                path="/youtube/videos/:videoId/digests/:version"
                element={<VideoDetail />}
              />
              {/* Back-compat for any bookmarks of the pre-6d flat routes. */}
              <Route path="/videos/:videoId" element={<VideoLegacyRedirect />} />
              <Route
                path="/videos/:videoId/digests/:version"
                element={<VideoLegacyRedirect />}
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </main>

        <ChatPanel open={chatOpen} onToggle={() => setChatOpen((v) => !v)} />
      </div>
    </div>
  );
}
